// src/pages/qc/PageQCWaterAnalysis.jsx
import { useState, useEffect, useMemo } from "react";
import * as api from "../../apiService";
import { WaterAnalysisModal } from "./WaterAnalysisModal";
import { fmtDateTime, currentMonthKey } from "../../utils/dateUtils";
import { MonthYearSelect } from "../../components/MonthYearSelect";
import { useToast } from "../../components/Toast";

const WATER_LABELS = { FEED: "Feed Water", RO: "RO Water", REJECT: "Reject Water" };
const WATER_COLORS = {
  FEED:   { bg: "#dbeafe", color: "#1e40af" },
  RO:     { bg: "#d1fae5", color: "#065f46" },
  REJECT: { bg: "#fee2e2", color: "#991b1b" },
};

function TypeBadge({ type }) {
  const c = WATER_COLORS[type] ?? { bg: "#f3f4f6", color: "#374151" };
  return (
    <span style={{ background: c.bg, color: c.color, borderRadius: 4,
      padding: "2px 8px", fontSize: 11, fontWeight: 600 }}>
      {WATER_LABELS[type] ?? type}
    </span>
  );
}

export function PageQCWaterAnalysis({ myPerms }) {
  const showToast = useToast();
  const [rows,    setRows]    = useState([]);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState(null);
  const [search,   setSearch]   = useState("");
  const [monthKey, setMonthKey] = useState(currentMonthKey);
  const [filter,   setFilter]   = useState("ALL");
  const [modal,   setModal]   = useState(false);

  const canEdit   = myPerms?.includes("QC_EDIT");
  const canDelete = myPerms?.includes("USER_MANAGE");

  useEffect(() => {
    api.fetchWaterAnalyses()
      .then(setRows)
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, []);

  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    return rows.filter(r => {
      const matchMonth = (r.date || "").startsWith(monthKey);
      const matchType  = filter === "ALL" || r.waterType === filter;
      const matchQ     = !q ||
        (WATER_LABELS[r.waterType] ?? "").toLowerCase().includes(q) ||
        (r.deliveredBy ?? "").toLowerCase().includes(q) ||
        (r.remarks ?? "").toLowerCase().includes(q);
      return matchMonth && matchType && matchQ;
    });
  }, [rows, search, filter, monthKey]);

  async function handleCreate(body) {
    const created = await api.createWaterAnalysis(body);
    setRows(old => [created, ...old]);
    setModal(false);
    showToast("Analysis saved");
  }

  async function handleDelete(id) {
    if (!window.confirm("Delete this water analysis record? This cannot be undone.")) return;
    await api.deleteWaterAnalysis(id);
    setRows(old => old.filter(r => r.id !== id));
    showToast("Record deleted", "info");
  }

  const colCount = 9 + (canDelete ? 1 : 0);

  return (
    <>
      <div className="page-header">
        <div className="page-header-left">
          <div className="page-title">Daily Water Analysis</div>
          <div className="page-subtitle">Feed, RO and Reject water — TDS · pH · Temperature</div>
        </div>
        {canEdit && (
          <div className="page-header-right">
            <button className="btn btn-primary" onClick={() => setModal(true)}>+ Add Entry</button>
          </div>
        )}
      </div>

      <div className="action-bar" style={{ display:"flex", gap:8, alignItems:"center" }}>
        <MonthYearSelect value={monthKey} onChange={setMonthKey} />
        <input className="form-input" placeholder="Search delivered by, remarks…"
          value={search} onChange={e => setSearch(e.target.value)} style={{ width:240 }} />
        <select className="form-select" value={filter} onChange={e => setFilter(e.target.value)}
          style={{ width:160 }}>
          <option value="ALL">All Types</option>
          <option value="FEED">Feed Water</option>
          <option value="RO">RO Water</option>
          <option value="REJECT">Reject Water</option>
        </select>
      </div>

      {loading && <div style={{ color:"#aaa", fontSize:12 }}>Loading…</div>}
      {error   && <div className="alert-warn">Error: {error}</div>}

      {!loading && !error && (
        <div className="table-wrap">
          <table className="data-table">
            <thead>
              <tr>
                <th>Date</th>
                <th>Water Type</th>
                <th>TDS (ppm)</th>
                <th>pH</th>
                <th>Temp (°C)</th>
                <th>Appearance</th>
                <th>Delivered By</th>
                <th>Recorded At</th>
                <th>Remarks</th>
                {canDelete && <th></th>}
              </tr>
            </thead>
            <tbody>
              {filtered.map(row => (
                <tr key={row.id}>
                  <td style={{ whiteSpace:"nowrap" }}>{row.date}</td>
                  <td><TypeBadge type={row.waterType} /></td>
                  <td>{row.tds != null ? String(row.tds) : "—"}</td>
                  <td>{row.ph  != null ? String(row.ph)  : "—"}</td>
                  <td>{row.tempC != null ? `${row.tempC}°C` : "—"}</td>
                  <td>{row.appearance  || "—"}</td>
                  <td>{row.deliveredBy || "—"}</td>
                  <td style={{ whiteSpace:"nowrap", fontSize:12, color:"#6b7280" }}>
                    {fmtDateTime(row.recordedAt)}
                  </td>
                  <td style={{ maxWidth:200, fontSize:12 }}>{row.remarks || "—"}</td>
                  {canDelete && (
                    <td>
                      <button className="btn-small btn-danger" onClick={() => handleDelete(row.id)}>Del</button>
                    </td>
                  )}
                </tr>
              ))}
              {filtered.length === 0 && (
                <tr>
                  <td colSpan={colCount} style={{ textAlign:"center", color:"#aaa", padding:24 }}>
                    No records found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {modal && (
        <div className="overlay" onClick={e => { if (e.target === e.currentTarget) setModal(false); }}>
          <WaterAnalysisModal onSave={handleCreate} onCancel={() => setModal(false)} />
        </div>
      )}
    </>
  );
}
